import React, { useState } from 'react';
import { SearchIcon, Trash2Icon, Edit2Icon, BellIcon, BellOffIcon } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';

const FREQUENCY_LABELS = {
  instant: 'Instant alerts',
  daily:   'Daily digest',
  weekly:  'Weekly digest',
};

const FREQUENCY_BLURB = {
  instant: 'Get notified as soon as a match publishes',
  daily:   'One email per day with all new matches',
  weekly:  'One email per week with all new matches',
};

function formatFilters(filters = {}) {
  const parts = [];
  if (filters.industry)              parts.push(String(filters.industry).replace(/_/g, ' '));
  if (filters.province)              parts.push(filters.province);
  if (filters.asking_price_range)    parts.push(filters.asking_price_range);
  if (filters.revenue_range)         parts.push(`Rev: ${filters.revenue_range}`);
  if (filters.employee_count_range)  parts.push(`Employees: ${filters.employee_count_range}`);
  if (filters.timeline)              parts.push(filters.timeline);
  if (filters.q)                     parts.push(`"${filters.q}"`);
  return parts.join(' · ') || 'All listings';
}

function FrequencyModal({ search, onClose, onSave }) {
  const [frequency, setFrequency] = useState(search.alert_frequency || 'daily');

  return (
    <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-[16px] p-6 max-w-md w-full space-y-6">
        <div>
          <h2 className="text-[18px] font-semibold text-foreground mb-1">Edit alert frequency</h2>
          <p className="text-[13px] text-muted-foreground">{search.name}</p>
        </div>

        <div className="space-y-3">
          <label className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            ALERT FREQUENCY
          </label>
          <div className="space-y-2">
            {['instant', 'daily', 'weekly'].map((value) => (
              <label key={value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="frequency"
                  value={value}
                  checked={frequency === value}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="text-[13px] font-medium text-foreground">{FREQUENCY_LABELS[value]}</div>
                  <div className="text-[12px] text-muted-foreground">{FREQUENCY_BLURB[value]}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
          <button onClick={onClose} className="border border-border text-foreground rounded-full px-5 py-2 text-[14px] hover:bg-accent transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onSave(frequency)}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] hover:opacity-90 transition-opacity"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SavedSearchesPage({
  savedSearches = [],
  loading = false,
  onUpdateFrequency,
  onDelete,
  persona = 'BUYER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
}) {
  const [editing, setEditing] = useState(null);

  const layoutProps = { persona, userName, userInitials, planProgress, actionPlanBadge };

  return (
    <AppLayout {...layoutProps}>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-[42px] font-light tracking-[-0.02em] text-foreground">Saved Searches</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your saved searches and alert preferences.
            </p>
          </div>
          <a href="/succeedo/marketplace" className="border border-border text-foreground rounded-full px-4 py-2 text-[14px] hover:bg-accent transition-colors">
            Browse Marketplace
          </a>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!loading && savedSearches.length === 0 && (
          <div className="bg-card border border-border rounded-[16px] p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <SearchIcon className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-[18px] font-semibold text-foreground mb-2">No saved searches yet</h3>
            <p className="text-[14px] text-muted-foreground mb-6 max-w-md mx-auto">
              Save a search from the marketplace to get notified when new matching listings publish.
            </p>
            <a href="/succeedo/marketplace" className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] hover:opacity-90 inline-block">
              Browse Marketplace
            </a>
          </div>
        )}

        {!loading && savedSearches.length > 0 && (
          <div className="space-y-4">
            {savedSearches.map((s) => {
              const FreqIcon = s.alert_frequency === 'instant' ? BellIcon : BellOffIcon;
              return (
                <div key={s.id} className="bg-card border border-border rounded-[16px] p-6">
                  <div className="mb-4">
                    <h3 className="text-[18px] font-semibold text-foreground mb-2">{s.name}</h3>
                    <p className="text-[13px] text-muted-foreground mb-3">{formatFilters(s.filters)}</p>
                    <div className="flex items-center gap-4 text-[12px] text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <FreqIcon className="w-3.5 h-3.5" />
                        <span>{FREQUENCY_LABELS[s.alert_frequency] || s.alert_frequency}</span>
                      </div>
                      {s.last_alert_sent_at && (
                        <div>Last alert: {new Date(s.last_alert_sent_at).toLocaleDateString()}</div>
                      )}
                      <div className="font-medium text-foreground">
                        {s.matches_this_week || 0} {Number(s.matches_this_week) === 1 ? 'match' : 'matches'} this week
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-border flex-wrap">
                    <a
                      href="/succeedo/marketplace"
                      className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-2 text-[13px] hover:opacity-90 inline-flex items-center gap-2"
                    >
                      <SearchIcon className="w-4 h-4" />
                      Run search now
                    </a>
                    <button
                      type="button"
                      onClick={() => setEditing(s)}
                      className="border border-border text-foreground rounded-full px-4 py-2 text-[13px] hover:bg-accent inline-flex items-center gap-2"
                    >
                      <Edit2Icon className="w-4 h-4" />
                      Edit frequency
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("Delete this saved search? You'll stop receiving alerts.")) onDelete?.(s.id);
                      }}
                      className="border border-border text-destructive rounded-full px-4 py-2 text-[13px] hover:bg-destructive/10 inline-flex items-center gap-2 ml-auto"
                    >
                      <Trash2Icon className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editing && (
          <FrequencyModal
            search={editing}
            onClose={() => setEditing(null)}
            onSave={(freq) => {
              onUpdateFrequency?.(editing.id, freq);
              setEditing(null);
            }}
          />
        )}
      </div>
    </AppLayout>
  );
}
